import axios from "axios";

/**
 * Stable, machine-readable error categories. Each maps 1:1 to an i18n key
 * under the `errors.*` namespace so the UI can render a localized message
 * without inspecting HTTP status codes itself.
 */
export type ApiErrorCode =
  | "network"
  | "timeout"
  | "canceled"
  | "badRequest"
  | "unauthorized"
  | "forbidden"
  | "notFound"
  | "conflict"
  | "validation"
  | "rateLimited"
  | "server"
  | "unknown";

/** Map of field name -> list of validation messages (HTTP 422). */
export type FieldErrors = Record<string, string[]>;

export const BUSINESS_ERROR_CODES = [
  "VALIDATION_ERROR",
  "HTTP_ERROR",
  "INTERNAL_ERROR",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "INVALID_CREDENTIALS",
  "INVALID_REFRESH_TOKEN",
  "SESSION_REVOKED",
  "PASSWORD_RESET_REQUIRED",
  "TEMPORARY_PASSWORD_EXPIRED",
  "EMAIL_NOT_VERIFIED",
  "ACCOUNT_SUSPENDED",
  "ACCOUNT_DISABLED",
  "ACCOUNT_LOCKED",
  "NO_PORTAL_ACCESS",
  "EMAIL_ALREADY_EXISTS",
  "USER_NOT_FOUND",
  "ORG_NOT_FOUND",
  "INVITATION_NOT_FOUND",
  "INVITATION_INVALID",
  "INVALID_CONTEXT",
  "OTP_INVALID",
  "OTP_MAX_ATTEMPTS",
  "RATE_LIMITED",
  "ROLE_NOT_FOUND",
  "GEOGRAPHY_NOT_FOUND",
  "GEOGRAPHY_DUPLICATE_NAME",
  "GEOGRAPHY_DUPLICATE_CODE",
  "GEOGRAPHY_DUPLICATE_ISO_CODE",
  "GEOGRAPHY_HAS_ACTIVE_CHILDREN",
  "GEOGRAPHY_PARENT_INACTIVE",
  "GEOGRAPHY_DISTRICT_MISMATCH",
  "GEOGRAPHY_INVALID_MUNICIPALITIES",
  "PRICING_INVALID_CONTAINER_OPTION",
  "DISTRICT_SELECTION_REQUIRED",
  "DISTRICT_SELECTION_EMPTY",
  "COVERAGE_INVALID_DISTRICTS",
  "BULK_LIMIT_EXCEEDED",
  "ONBOARDING_SUBMISSION_PENDING",
  "ONBOARDING_INCOMPLETE",
  "UNIFIED_NUMBER_TAKEN",
  "FILE_PURPOSE_MISMATCH",
  "FILE_TOO_LARGE",
  "FILE_TYPE_NOT_ALLOWED",
  "FILE_NOT_FOUND",
  "UNSUPPORTED_IMAGE",
  "DOCUMENT_TYPE_NOT_FOUND",
  "VENDOR_DOCUMENT_EXPIRED",
  "VENDOR_DOCUMENT_SCOPE_INVALID",
  "VENDOR_DOCUMENT_PENDING_APPROVAL",
  "VENDOR_DOCUMENT_NOT_PENDING",
  "VENDOR_DOCUMENT_NOT_FOUND",
  "VENDOR_ORG_STATUS_CHANGED",
  "ONBOARDING_DOCUMENTS_INCOMPLETE",
  "ACTIVATION_INCOMPLETE",
  "ACTIVATION_PRECHECK_MISSING",
  "VENDOR_ONBOARDING_STATE_CHANGED",
  "DELIVERY_SLOT_NOT_FOUND",
  "DELIVERY_SLOT_CODE_EXISTS",
  "DELIVERY_TIME_SLOT_NOT_AVAILABLE",
  "REQUEST_INCOMPLETE",
  "REQUEST_MODIFIED_CONCURRENTLY",
  "REQUEST_NOT_EDITABLE",
  "REQUEST_HAS_ACTIVE_ORDER",
  "REQUEST_NOT_CANCELLABLE",
  "REQUEST_NOT_MATCHED",
  "REQUEST_SITE_IMAGES_NOT_EDITABLE",
  "REQUEST_SITE_IMAGE_ALREADY_LINKED",
  "REQUEST_NOT_REOPENABLE",
  "REQUEST_MODE_SWITCH_NOT_ALLOWED",
  "REQUEST_SERVICE_IMMUTABLE",
  "REQUEST_LOCATION_CONFLICT",
  "LOCATION_ARCHIVED",
  "PRICE_CHANGED",
  "BID_CHANGED",
  "BID_NOT_SELECTABLE",
  "BID_NOT_FOUND",
  "BIDDING_REQUEST_NOT_FOUND",
  "BIDDING_NOT_OPEN",
  "BID_ALREADY_SUBMITTED",
  "BID_NOT_EDITABLE",
  "BID_NOT_ELIGIBLE",
  "PENDING_ORDER_LIMIT_REACHED",
  "VENDOR_ALREADY_SELECTED",
  "VENDOR_NOT_ELIGIBLE",
  "CATALOG_NOT_FOUND",
  "CATALOG_DUPLICATE_CODE",
  "CATALOG_HAS_ACTIVE_CHILDREN",
  "CATALOG_PARENT_INACTIVE",
  "CATALOG_INVALID_OPTION_VALUE",
  "CATALOG_INVALID_OPTION_VALIDATION",
  "READINESS_RULE_NOT_FOUND",
  "READINESS_RULE_ARCHIVED",
  "READINESS_INVALID_RULE",
  "READINESS_DUPLICATE_CODE",
  "READINESS_DUPLICATE_RULE_TYPE",
  "VENDOR_CONTEXT_REQUIRED",
  "VENDOR_ORG_NOT_FOUND",
  "VENDOR_ORG_NOT_ACTIVE",
  "VENDOR_ONBOARDING_NOT_EDITABLE",
  "VENDOR_ONBOARDING_INCOMPLETE",
  "VENDOR_ONBOARDING_PENDING_APPROVAL",
  "VENDOR_SERVICE_NOT_FOUND",
  "APPROVAL_NOT_FOUND",
  "APPROVAL_ALREADY_DECIDED",
  "APPROVAL_REASON_REQUIRED",
  "APPROVAL_INVALID_ACTION",
  "APPROVAL_ITEM_COMMENT_INVALID",
  "APPROVAL_ITEM_COMMENT_EMPTY",
  "APPROVAL_SUBJECT_FORBIDDEN",
  "APPROVAL_SUBJECT_UNSUPPORTED",
  "APPROVAL_NOT_CANCELLABLE",
  "ORDER_NOT_FOUND",
  "ORDER_NOT_CANCELLABLE",
  "ORDER_NOT_AWAITING_RESPONSE",
  "ORDER_NO_LONGER_AVAILABLE",
  "ORDER_SLA_EXPIRED",
  "ORDER_NOT_EXECUTABLE",
  "WORK_ORDER_NOT_FOUND",
  "WORK_ORDER_NOT_ASSIGNABLE",
  "WORK_ORDER_NOT_ASSIGNED",
  "WORK_ORDER_NOT_IN_PROGRESS",
  "WORK_ORDER_CLOSED",
  "WORK_ORDER_CANCELLED",
  "WORK_ORDER_PROOF_REQUIRED",
  "WORK_ORDER_PROOF_LIMIT",
  "WORK_ORDER_PROOF_ALREADY_LINKED",
  "ASSET_NOT_FOUND",
  "ASSET_NOT_READY",
  "CONTAINER_REQUIRED",
  "INVALID_REJECTION_REASON",
  "REJECTION_COMMENT_REQUIRED",
  "ORDER_NOT_PAYABLE",
  "ORDER_PAYMENT_WINDOW_EXPIRED",
  "INSUFFICIENT_WALLET_BALANCE",
  "SPENDING_LIMIT_EXCEEDED",
  "PAYMENT_ALREADY_SETTLED",
  "INVALID_WEBHOOK_TOKEN",
  "REFUND_EXCEEDS_REMAINING_AMOUNT",
  "CARD_PAYMENT_NOT_ALLOWED_FOR_ORGANIZATION",
  "PAYMENT_WINDOW_TOO_SHORT",
  "PAYMENT_ATTEMPT_SUPERSEDED",
  "APPROVAL_SUBJECT_HANDLER_MISSING",
  "ASSET_ALREADY_APPROVED",
  "ASSET_APPROVAL_PENDING",
  "CANNOT_DEACTIVATE_SELF",
  "CANNOT_EDIT_OWN_ROLES",
  "CHARGE_CALLBACK_CONTEXT_REQUIRED",
  "CHARGE_REFERENCE_TYPE_NOT_IMPLEMENTED",
  "CONTAINER_CODE_EXISTS",
  "CONTAINER_NOT_FOUND",
  "DOCUMENT_TYPE_CODE_EXISTS",
  "DOCUMENT_TYPE_IN_USE",
  "DOCUMENT_TYPE_SCOPE_INVALID",
  "DRIVER_LICENSE_EXISTS",
  "DRIVER_NOT_FOUND",
  "FILE_ACCESS_FORBIDDEN",
  "FILE_REQUIRED",
  "FILE_UPLOAD_INVALID",
  "INSUFFICIENT_PRIVILEGE",
  "INVALID_CHARGE_AMOUNT",
  "INVALID_PLATFORM_ROLE",
  "INVALID_REFUND_AMOUNT",
  "INVALID_SERVICE_DATE",
  "INVALID_WALLET_AMOUNT",
  "INVITATION_ROLES_INVALID",
  "INVITE_NOT_PENDING",
  "INVOICE_NOT_FOUND",
  "INVOICE_PDF_UNAVAILABLE",
  "LAST_SUPER_ADMIN",
  "LOCATION_COORDINATES_INCOMPLETE",
  "LOCATION_MAP_OR_COORDINATES",
  "LOCATION_NOT_FOUND",
  "MEMBERSHIP_CONTEXT_REQUIRED",
  "MEMBERSHIP_NOT_FOUND",
  "MODULE_NOT_ASSIGNABLE",
  "MODULE_NOT_ENABLED",
  "MODULE_NOT_FOUND",
  "NOTIFICATION_NOT_FOUND",
  "ORDER_ALREADY_PAID",
  "ORG_CONTEXT_REQUIRED",
  "ORG_NOT_ENTITLEMENT_ELIGIBLE",
  "PAYMENT_GATEWAY_ERROR",
  "PAYMENT_GATEWAY_INVOICE_MISSING",
  "PAYMENT_GATEWAY_INVOICE_NOT_FOUND",
  "PAYMENT_GATEWAY_NOT_FOUND",
  "PAYMENT_GATEWAY_PAYMENT_NOT_FOUND",
  "PAYMENT_GATEWAY_REFUND_REJECTED",
  "PAYMENT_GATEWAY_UNAVAILABLE",
  "PAYMENT_NOT_FOUND",
  "PAYMENT_NOT_REFUNDABLE",
  "PAYMENT_RETURN_URL_NOT_CONFIGURED",
  "PERMISSION_NOT_ASSIGNABLE",
  "PERMISSION_NOT_FOUND",
  "PLATFORM_MEMBERSHIP_INACTIVE",
  "PLATFORM_PERMISSION_NOT_ASSIGNABLE",
  "PLATFORM_USER_DEACTIVATED",
  "PLATFORM_USER_NOT_FOUND",
  "REFUND_GATEWAY_OUTCOME_UNKNOWN",
  "REFUND_NOT_CONFIRMABLE",
  "REFUND_NOT_FOUND",
  "REFUND_REASON_RESERVED",
  "REQUEST_NOT_DRAFT",
  "REQUEST_NOT_FOUND",
  "REQUEST_OPTIONS_DUPLICATE",
  "REQUEST_OPTIONS_DUPLICATE_TYPE",
  "REQUEST_OPTION_INACTIVE",
  "REQUEST_OPTION_SERVICE_MISMATCH",
  "ROLE_CODE_REQUIRED",
  "ROLE_CODE_TAKEN",
  "ROLE_IN_USE",
  "ROLE_NAME_TAKEN",
  "SERVICE_NOT_FOUND",
  "SYSTEM_ROLE_READ_ONLY",
  "TRUCK_NOT_FOUND",
  "TRUCK_PLATE_EXISTS",
  "UNSUPPORTED_CHARGE_CURRENCY",
  "UNSUPPORTED_PAYMENT_METHOD",
  "USER_ALREADY_DEACTIVATED",
  "USER_DEACTIVATED",
  "USER_MODIFIED_CONCURRENTLY",
  "USER_NOT_DEACTIVATED",
  "VENDOR_SERVICE_ACTIVATION_PENDING",
  "VENDOR_SERVICE_ALREADY_SELECTED",
  "VENDOR_SERVICE_ALREADY_SUSPENDED",
  "VENDOR_SERVICE_CANNOT_ARCHIVE",
  "VENDOR_SERVICE_NOT_READY",
  "VENDOR_SERVICE_NOT_SUBMITTABLE",
  "VENDOR_SERVICE_NOT_SUSPENDED",
  "WALLET_ATTACHMENT_ALREADY_LINKED",
  "WALLET_ATTACHMENT_REQUIRED",
  "WALLET_NOT_FOUND",
  "WALLET_OWNER_NOT_FOUND",
  "WALLET_TOPUP_AMOUNT_TOO_SMALL",
  "WALLET_TOPUP_NOT_FOUND",
  "WALLET_TRANSACTION_NOT_FOUND",
  "WEBHOOK_CALLBACK_NOT_FOUND",
] as const;

/**
 * Stable business/protocol error codes returned by the backend in the
 * `errorCode` field of the standard error envelope. Unlike {@link ApiErrorCode}
 * (which is derived from HTTP status), these describe *product* behavior and
 * are the contract the UI should branch on.
 */
export type BusinessErrorCode = (typeof BUSINESS_ERROR_CODES)[number];

/** i18n key namespace prefix used for every {@link ApiErrorCode}. */
const I18N_PREFIX = "errors";

/**
 * Normalized error shape used across all EnviroHub front-ends. Any failure
 * (axios, network, timeout, or unexpected throw) is converted into one of
 * these so consumers never have to branch on raw axios internals.
 */
export class ApiError extends Error {
  /** HTTP status when available, otherwise `null` (network/timeout/etc.). */
  readonly status: number | null;

  /** Machine-readable category derived from HTTP status. */
  readonly code: ApiErrorCode;

  /**
   * Stable business/protocol error code from the backend envelope, when
   * present. This is the contract the UI should branch on for product
   * behavior (see {@link BusinessErrorCode}).
   */
  readonly errorCode?: BusinessErrorCode;

  /** i18n key, e.g. `errors.notFound`. */
  readonly i18nKey: string;

  /** Raw message returned by the backend, if any (may be unlocalized). */
  readonly serverMessage?: string;

  /** Per-field validation messages for 422 responses. */
  readonly fieldErrors?: FieldErrors;

  /** Original error for logging/debugging. */
  readonly cause?: unknown;

  constructor(params: {
    code: ApiErrorCode;
    status?: number | null;
    errorCode?: BusinessErrorCode;
    serverMessage?: string;
    fieldErrors?: FieldErrors;
    cause?: unknown;
  }) {
    super(params.serverMessage ?? params.errorCode ?? params.code);
    this.name = "ApiError";
    this.code = params.code;
    this.status = params.status ?? null;
    this.errorCode = params.errorCode;
    this.i18nKey = `${I18N_PREFIX}.${params.code}`;
    this.serverMessage = params.serverMessage;
    this.fieldErrors = params.fieldErrors;
    this.cause = params.cause;
  }
}

/** Type guard for {@link ApiError}. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Map an HTTP status code to a stable {@link ApiErrorCode}. */
function codeFromStatus(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return "badRequest";
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "notFound";
    case 409:
      return "conflict";
    case 422:
      return "validation";
    case 429:
      return "rateLimited";
    default:
      if (status >= 500) return "server";
      if (status >= 400) return "badRequest";
      return "unknown";
  }
}

/**
 * Add a single field message into a {@link FieldErrors} accumulator.
 */
function pushFieldError(target: FieldErrors, field: string, message: string): void {
  if (!field || !message) return;
  /* eslint-disable no-param-reassign -- accumulator pattern */
  if (target[field]) target[field].push(message);
  else target[field] = [message];
  /* eslint-enable no-param-reassign */
}

/**
 * Normalize the backend `errors` payload into a {@link FieldErrors} map.
 * Supports two shapes:
 *   1. Array of `{ field|path|name, message }` entries.
 *   2. Object map of `field -> string | string[]`.
 */
function parseFieldErrors(raw: unknown): FieldErrors | undefined {
  if (!raw) return undefined;

  const fieldErrors: FieldErrors = {};

  /* eslint-disable no-restricted-syntax, no-continue -- clear iteration over heterogeneous error payloads */
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (!entry || typeof entry !== "object") continue;
      const item = entry as Record<string, unknown>;
      const field =
        (typeof item.field === "string" && item.field) ||
        (typeof item.path === "string" && item.path) ||
        (typeof item.name === "string" && item.name) ||
        "";
      const message =
        (typeof item.message === "string" && item.message) ||
        (typeof item.error === "string" && item.error) ||
        "";
      if (field && message) {
        pushFieldError(fieldErrors, field, message);
        continue;
      }
      // CR1 REQUEST_INCOMPLETE: [{ "serviceStartDate": "…" }, …]
      for (const [key, value] of Object.entries(item)) {
        if (
          key === "field" ||
          key === "path" ||
          key === "name" ||
          key === "message" ||
          key === "error"
        ) {
          continue;
        }
        if (typeof value === "string") pushFieldError(fieldErrors, key, value);
        else if (Array.isArray(value)) {
          for (const v of value) pushFieldError(fieldErrors, key, String(v));
        }
      }
    }
  } else if (typeof raw === "object") {
    for (const [field, value] of Object.entries(raw as Record<string, unknown>)) {
      if (Array.isArray(value)) {
        for (const v of value) pushFieldError(fieldErrors, field, String(v));
      } else if (typeof value === "string") {
        pushFieldError(fieldErrors, field, value);
      }
    }
  }
  /* eslint-enable no-restricted-syntax, no-continue */

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
}

/**
 * Best-effort extraction of the backend error envelope. Standard shape:
 *   { success: false; statusCode: number; errorCode: string; message: string; errors?: [] }
 * Falls back gracefully when the body is a string or unrecognized.
 */
function parseBody(data: unknown): {
  message?: string;
  errorCode?: BusinessErrorCode;
  fieldErrors?: FieldErrors;
} {
  if (!data) return {};
  if (typeof data === "string") return { message: data };
  if (typeof data !== "object") return {};

  const body = data as Record<string, unknown>;
  const message =
    (typeof body.message === "string" && body.message) ||
    (typeof body.error === "string" && body.error) ||
    (typeof body.detail === "string" && body.detail) ||
    undefined;

  const errorCode =
    typeof body.errorCode === "string" ? (body.errorCode as BusinessErrorCode) : undefined;

  const fieldErrors = parseFieldErrors(body.errors ?? body.fieldErrors);

  return { message, errorCode, fieldErrors };
}

/**
 * Convert ANY thrown value into a normalized {@link ApiError}. This is the
 * single source of truth for error categorization in the app.
 */
export function normalizeError(error: unknown): ApiError {
  if (isApiError(error)) return error;

  if (axios.isAxiosError(error)) {
    // Request was canceled (e.g. component unmounted / query aborted).
    if (axios.isCancel?.(error) || error.code === "ERR_CANCELED") {
      return new ApiError({ code: "canceled", cause: error });
    }

    // Timeout.
    if (error.code === "ECONNABORTED" || /timeout/i.test(error.message)) {
      return new ApiError({ code: "timeout", cause: error });
    }

    // No response received -> network / server unreachable.
    if (!error.response) {
      return new ApiError({ code: "network", cause: error });
    }

    const { status } = error.response;
    const { message, errorCode, fieldErrors } = parseBody(error.response.data);
    return new ApiError({
      code: codeFromStatus(status),
      status,
      errorCode,
      serverMessage: message,
      fieldErrors,
      cause: error,
    });
  }

  if (error instanceof Error) {
    return new ApiError({ code: "unknown", serverMessage: error.message, cause: error });
  }

  return new ApiError({ code: "unknown", cause: error });
}

/**
 * Resolve a user-facing, localized message for an error. Pass the i18next
 * `t` function. Prefers the localized key, then a backend-provided message,
 * then a generic fallback.
 */
export function getApiErrorMessage(
  error: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const apiError = normalizeError(error);

  // Business error codes are more specific than HTTP category keys.
  if (apiError.errorCode) {
    const codeMessage = t(`${I18N_PREFIX}.codes.${apiError.errorCode}`, {
      defaultValue: apiError.serverMessage ?? "",
    });
    if (codeMessage) return codeMessage;
  }

  // For validation/unknown errors the backend message is usually the most
  // specific; for everything else prefer the localized category message.
  const preferServerMessage = apiError.code === "validation" || apiError.code === "unknown";

  const localized = t(apiError.i18nKey, {
    defaultValue: apiError.serverMessage ?? "",
  });

  if (preferServerMessage && apiError.serverMessage) {
    return apiError.serverMessage;
  }
  return localized || apiError.serverMessage || t(`${I18N_PREFIX}.unknown`);
}
